import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Mail, Phone, Bell, MessageSquare } from 'lucide-react';
import { API } from '../App';

function Auth({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    announcements_email: false,
    announcements_text: false,
    event_reminders_email: false,
    event_reminders_text: false
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { ...formData, notification_preferences: notificationPrefs };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || `${isLogin ? 'Login' : 'Registration'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const toggleNotificationPref = (key) => {
    setNotificationPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fffbeb' }}>
      <div className="max-w-md w-full">
        <div className="card p-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="auth-title">
              {isLogin ? 'Welcome Back' : 'Join Us'}
            </h2>
            <p className="text-lg text-slate-600 mt-2">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>First Name</label>
                  <div className="relative">
                    <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" color="#94a3b8" />
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="John"
                      data-testid="first-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Last Name</label>
                  <div className="relative">
                    <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" color="#94a3b8" />
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Doe"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Phone (Optional)</label>
                  <div className="relative">
                    <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" color="#94a3b8" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email</label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" color="#94a3b8" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Password</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" color="#94a3b8" />
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="Enter your password"
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Notification Preferences - Only show during registration */}
            {!isLogin && (
              <div className="space-y-4">
                <label className="block text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                  <Bell size={20} className="inline mr-2" />
                  Notification Preferences
                </label>
                <p className="text-sm text-slate-600">Choose how you'd like to stay connected with our church community:</p>
                
                {/* Announcements Notifications */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-amber-900 mb-2">📢 New Announcements</h4>
                  <p className="text-xs text-amber-700 mb-3">Get notified when new church announcements are posted</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.announcements_email}
                        onChange={() => toggleNotificationPref('announcements_email')}
                        className="w-5 h-5 text-amber-700 rounded focus:ring-amber-500"
                      />
                      <Mail size={16} className="text-amber-700" />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.announcements_text}
                        onChange={() => toggleNotificationPref('announcements_text')}
                        className="w-5 h-5 text-amber-700 rounded focus:ring-amber-500"
                      />
                      <MessageSquare size={16} className="text-amber-700" />
                      <span className="text-sm">Text Message</span>
                    </label>
                  </div>
                </div>

                {/* Event Reminders */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">📅 Event Reminders</h4>
                  <p className="text-xs text-blue-700 mb-3">Get reminded the day before each upcoming event (includes event name, time & location)</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.event_reminders_email}
                        onChange={() => toggleNotificationPref('event_reminders_email')}
                        className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900"
                      />
                      <Mail size={16} className="text-blue-700" />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPrefs.event_reminders_text}
                        onChange={() => toggleNotificationPref('event_reminders_text')}
                        className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900"
                      />
                      <MessageSquare size={16} className="text-blue-700" />
                      <span className="text-sm">Text Message</span>
                    </label>
                  </div>
                </div>

                {/* SMS Consent Disclosure */}
                {(notificationPrefs.announcements_text || notificationPrefs.event_reminders_text) && (
                  <div className="bg-gray-100 p-4 rounded-lg border border-gray-300" data-testid="sms-consent-disclosure">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <strong>SMS Consent:</strong> By checking the text message boxes above, you agree to receive text messages from Coos Bay Cornerstone Church regarding church announcements, event reminders, and community updates. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help. Message frequency varies. Message and data rates may apply. View our <a href="/privacy-policy" className="text-blue-600 underline hover:text-blue-800">Privacy Policy</a> for more information.
                    </p>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="submit-auth-btn"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-lg text-blue-900 hover:text-amber-500 font-semibold"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Auth;
